import React, { useState, useEffect } from "react"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { ToastContainer, toast } from "react-toastify"
import ReactPaginate from "react-paginate"
import classnames from "classnames"
import { URLS } from "../../Url"
import moment from "moment"
import axios from "axios"
import {
  Nav,
  Row,
  Col,
  Card,
  Table,
  NavItem,
  NavLink,
  CardBody,
} from "reactstrap"

const Notifications = () => {
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [activeTab, setActiveTab] = useState("All")
  const [pageNumber, setPageNumber] = useState(0)
  const [listPerPage] = useState(5)

  const authUser = JSON.parse(localStorage.getItem("authUser"))
  const token = authUser?.token || ""

  const fetchNotifications = async (isRead = "All") => {
    try {
      setLoading(true)
      const res = await axios.post(
        URLS.GetNotifications,
        { isRead: isRead, userType: "Supplier" },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setNotifications(
        Array.isArray(res.data?.notifResult) ? res.data.notifResult : []
      )
      setPageNumber(0)
    } catch (error) {
      toast.error("Failed to fetch notifications")
      console.error("Notification fetch error:", error)
      setNotifications([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNotifications("All")
  }, [])

  const handleTabChange = tab => {
    setActiveTab(tab)
    if (tab === "All") {
      fetchNotifications("All")
    } else if (tab === "Read") {
      fetchNotifications(true)
    } else if (tab === "Unread") {
      fetchNotifications(false)
    }
  }

  const getFilteredNotifications = () => {
    if (!Array.isArray(notifications)) {
      return []
    }
    let filtered = notifications.filter(notification => {
      const title = notification?.title || ""
      const description = notification?.description || ""
      return (
        title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })
    if (activeTab === "Read") {
      filtered = filtered.filter(notification => notification?.isRead === true)
    } else if (activeTab === "Unread") {
      filtered = filtered.filter(notification => notification?.isRead !== true)
    }
    return filtered
  }

  const filteredNotifications = getFilteredNotifications()
  const pagesVisited = pageNumber * listPerPage
  const pageCount = Math.ceil(filteredNotifications.length / listPerPage)
  const displayedNotifications = filteredNotifications.slice(
    pagesVisited,
    pagesVisited + listPerPage
  )

  const changePage = ({ selected }) => {
    setPageNumber(selected)
  }

  const markAsRead = async (id, isCurrentlyRead) => {
    if (isCurrentlyRead) return
    try {
      const res = await axios.post(
        URLS.MarkNotificationAsRead,
        { _id: id },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      if (res.status === 200) {
        setNotifications(prevNotifications =>
          prevNotifications.map(notification =>
            notification._id === id
              ? { ...notification, isRead: true }
              : notification
          )
        )
      }
    } catch (error) {
      console.error("Mark as read error:", error)
    }
  }

  const deleteNotification = async id => {
    try {
      const res = await axios.post(
        URLS.DeleteNotification,
        { _id: id },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      if (res.status === 200) {
        toast.success(res.data.message || "Notification deleted successfully")
        if (activeTab === "All") {
          fetchNotifications("All")
        } else if (activeTab === "Read") {
          fetchNotifications(true)
        } else if (activeTab === "Unread") {
          fetchNotifications(false)
        }
      }
    } catch (error) {
      if (error.response) {
        toast.error(
          error.response.data?.message || "Failed to delete notification"
        )
      } else {
        toast.error("Network error occurred")
      }
      console.error("Delete notification error:", error)
    }
  }

  const confirmDelete = data => {
    if (window.confirm("Do you really want to delete this notification?")) {
      deleteNotification(data._id)
    }
  }

  const getTabCount = tab => {
    if (!Array.isArray(notifications)) return 0
    if (tab === "All") {
      return notifications.filter(n => n !== null && n !== undefined).length
    } else if (tab === "Read") {
      return notifications.filter(n => n?.isRead === true).length
    } else if (tab === "Unread") {
      return notifications.filter(n => n?.isRead !== true).length
    }
    return 0
  }

  const formatDate = dateString => {
    if (!dateString) return "N/A"
    try {
      const date = moment(dateString)
      if (date.isValid()) {
        return date.format("MMM D, YYYY h:mm A")
      }
      return "Invalid Date"
    } catch (error) {
      return "N/A"
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title="Bizzmate Supplier"
            breadcrumbItem="Notifications"
          />
          <Row className="mb-4">
            <Col md={12}>
              <Card className="shadow-sm">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h4 className="card-title mb-0">Notifications</h4>
                    <div className="d-flex align-items-center">
                      <div className="search-box ms-2">
                        <div className="position-relative">
                          <input
                            type="text"
                            className="form-control bg-light border-light rounded"
                            placeholder="Search notifications..."
                            value={searchTerm}
                            onChange={e => {
                              setSearchTerm(e.target.value)
                              setPageNumber(0)
                            }}
                          />
                          <i className="bx bx-search-alt search-icon"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Nav tabs className="nav-tabs-custom mb-4">
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "All" })}
                        onClick={() => handleTabChange("All")}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="bx bx-list-ul me-1"></i> All (
                        <b className="text-primary ms-1">
                          {getTabCount("All")}
                        </b>
                        )
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "Read" })}
                        onClick={() => handleTabChange("Read")}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="bx bx-check-circle me-1"></i> Read (
                        <b className="text-primary ms-1">
                          {getTabCount("Read")}
                        </b>
                        )
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({
                          active: activeTab === "Unread",
                        })}
                        onClick={() => handleTabChange("Unread")}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="bx bx-bell me-1"></i> Unread (
                        <b className="text-primary ms-1">
                          {getTabCount("Unread")}
                        </b>
                        )
                      </NavLink>
                    </NavItem>
                  </Nav>
                  {loading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading notifications...</p>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table hover className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Status</th>
                              <th>Title</th>
                              <th>Description</th>
                              <th>Date</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedNotifications.length > 0 ? (
                              displayedNotifications.map(notification => (
                                <tr
                                  key={notification?._id || Math.random()}
                                  className={
                                    notification?.isRead ? "" : "table-info"
                                  }
                                  onClick={() =>
                                    markAsRead(
                                      notification?._id,
                                      notification?.isRead
                                    )
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <td>
                                    <span
                                      className={
                                        notification?.isRead
                                          ? "text-success"
                                          : "text-warning"
                                      }
                                      style={{ fontWeight: "bold" }}
                                    >
                                      <i
                                        className={`bx ${
                                          notification?.isRead
                                            ? "bx-check-circle"
                                            : "bx-bell"
                                        } me-1`}
                                      ></i>
                                      {notification?.isRead ? "Read" : "Unread"}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <span className="me-2 text-primary">
                                        <i className="bx bx-bell"></i>
                                      </span>
                                      <span
                                        className={`${
                                          !notification?.isRead
                                            ? "text-primary"
                                            : ""
                                        }`}
                                        style={{ fontWeight: "500" }}
                                      >
                                        {notification?.title || "No Title"}
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="text-wrap">
                                      {notification?.description ||
                                        "No Description"}
                                    </div>
                                  </td>
                                  <td>
                                    {formatDate(notification?.logCreatedDate)}
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <i
                                        className="bx bx-trash text-primary"
                                        style={{
                                          fontSize: "22px",
                                          cursor: "pointer",
                                        }}
                                        onClick={e => {
                                          e.stopPropagation()
                                          confirmDelete(notification)
                                        }}
                                        title="Delete Notification"
                                      ></i>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="5" className="text-center py-4">
                                  <div className="py-4">
                                    <i className="bx bx-bell-off display-4 text-muted mb-3"></i>
                                    <h5>No notifications found</h5>
                                    <p className="text-muted">
                                      {activeTab === "All" &&
                                        "You don't have any notifications"}
                                      {activeTab === "Read" &&
                                        "No read notifications"}
                                      {activeTab === "Unread" &&
                                        "No unread notifications"}
                                      {searchTerm &&
                                        " - Try adjusting your search"}
                                    </p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                      {pageCount > 1 && (
                        <div className="d-flex justify-content-end mt-3">
                          <ReactPaginate
                            previousLabel={"Previous"}
                            nextLabel={"Next"}
                            pageCount={pageCount}
                            onPageChange={changePage}
                            containerClassName={"pagination"}
                            previousLinkClassName={"page-link"}
                            nextLinkClassName={"page-link"}
                            disabledClassName={"disabled"}
                            activeClassName={"active"}
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                          />
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </React.Fragment>
  )
}

export default Notifications
